import React from 'react';
import { withRouter } from 'react-router-dom';
import _get from 'lodash/get';
import { v4 as uuidv4 } from 'uuid';

import { Loader } from 'components';
import { user, localStore } from 'utils';
import { logError } from 'utils/extra';
import { identifyUser } from 'utils/analytics';
import { attachCustomScripts } from 'utils/retailer';
import { getMasterData } from 'utils/services';
import {
  formatAndStoreMasterData,
  formatStoreProperties,
  loadJsCssFile
} from 'utils/methods';
import fetchStoreProperties from '../config/setup/fetchStoreProperties';
import {
  RETURN_AND_EXCHANGE,
  ONLY_RETURN,
  BASIC_MODE_ECOM_PLATFORM
} from '../dictionary';

const DEMO_APP_SUBDOMAIN = 'instant-demo';

const AuthContext = React.createContext();

class AuthProvider extends React.Component {
  state = {
    isReady: false,
    masterData: null,
    isLoggedIn: user.isLoggedIn(),
    authToken: user.getToken(),
    hasError: false,
    errorObject: null,
    userData: user.get(),
    isLoadingStoreProperties: true,
    serviceCopies: {},
    aspenBenefits: [],
    allowPhoneLogin: false,
    inputLoginCombo: [],
    allowsExchange: true,
    allowsDiscountForwarding: true,
    canCreateRequest: true,
    isDemoApp: false,
    storeProperties: null
  };
  storeDetails = {};
  loginOrderID = null;
  appType = {
    retex: process.env.REACT_APP_TYPE !== 'SHIPMENT_TRACKING',
    shipmentTracking: process.env.REACT_APP_TYPE === 'SHIPMENT_TRACKING'
  };
  uid = uuidv4();

  componentDidMount() {
    this.bindEvents();
    this.getStoreDetails();
    if (this.appType.retex) {
      this.fetchMasterData();
      this.attachTheme();
      this.attachScripts();
      window.SolvvySettings = { position: 'bottom-left' };
    } else {
      this.setState({
        isReady: true,
        isLoadingStoreProperties: false
      });
    }

    this.checkIfDemoApp();
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      isReady,
      hasError,
      masterData,
      isLoadingStoreProperties,
      storeProperties
    } = this.state;
    if (hasError && prevProps.location !== this.props.location) {
      this.setState({
        hasError: false,
        errorObject: null
      });
    }
    if (
      isReady &&
      masterData &&
      storeProperties &&
      !isLoadingStoreProperties &&
      prevState.isLoadingStoreProperties
    ) {
      const ecommPlatform = _get(storeProperties, 'ecommerce_platform.source');

      if (ecommPlatform) {
        this.setState(currState => {
          const { masterData } = formatAndStoreMasterData(
            currState.masterData,
            ecommPlatform === 'BIGCOMMERCE'
          );
          return { masterData };
        });
      }
    }
  }

  bindEvents = () => {
    window.onerror = (message, source, lineno, colno, error) => {
      this.error(error);
    };
  };

  checkIfDemoApp = () => {
    const host = window.location.host;

    const [subDomain = ''] = host.split('.');
    const isDemoApp = subDomain === DEMO_APP_SUBDOMAIN;

    this.setState({
      isDemoApp
    });
  };

  fetchMasterData = () => {
    getMasterData(
      masterData => this.setState({ masterData, isReady: true }),
      () => this.setState({ isReady: true })
    );
  };

  getStoreDetails = () => {
    fetchStoreProperties(
      response => {
        const data = JSON.parse(response);
        const storeDetailsObject = formatStoreProperties(data);
        const allowsExchange = _get(data, 'is_exchange_allowed', true);
        const canCreateRequest = _get(data, 'can_create_request', false);
        const allowPhoneLogin = _get(data, 'allow_phone_number_login', false);
        const ecomPlatform = _get(data, 'ecommerce_platform.source', '');
        const isBasicMode = ecomPlatform === BASIC_MODE_ECOM_PLATFORM;
        const allowsDiscountForwarding = _get(
          data,
          'is_discount_forwarding_enabled',
          false
        );
        const { serviceCopies, aspenBenefits } =
          !isBasicMode && allowsExchange ? RETURN_AND_EXCHANGE : ONLY_RETURN;
        this.storeDetails = storeDetailsObject;
        this.setState({
          aspenBenefits,
          serviceCopies,
          allowsExchange,
          allowPhoneLogin,
          canCreateRequest,
          storeProperties: data,
          allowsDiscountForwarding,
          isLoadingStoreProperties: false
        });
      },
      err => {
        this.storeDetails = {};
        this.error(err);
        this.setState({ isLoadingStoreProperties: false });
      }
    );
  };

  attachTheme = () => {
    const [retailer] = window.location.host.split(':');
    const assetsURL = process.env.REACT_APP_STATIC_ASSETS_URL;
    if (assetsURL) {
      // const retailer = 'zenwear.testaspen.com';
      loadJsCssFile(
        `${assetsURL}/assets/store-theme/styles/${retailer}.css?v=${this.uid}`,
        'css'
      );
    }
  };

  attachScripts = () => attachCustomScripts();

  getLoginOrderID = () => {
    // Clears loginOrderID after it is accessed one time
    const loginOrderID = this.loginOrderID;
    if (!loginOrderID) return null;
    this.loginOrderID = null;
    return loginOrderID;
  };

  login = data => {
    try {
      localStore.put('authToken', data.accessToken);
      localStore.put('retailer', data.retailer_name);
      localStore.put('userData', JSON.stringify(data.userData));

      identifyUser();

      const orderName = _get(data, 'retailerOrderId', '');
      this.loginOrderID = orderName ? encodeURIComponent(orderName) : null;
      this.setState({
        isLoggedIn: true,
        userData: data.userData,
        authToken: data.accessToken
      });
    } catch (e) {
      logError(e);
    }
  };

  logout = (callback = () => {}) => {
    user.logout();
    sessionStorage.clear();
    this.loginOrderID = null;
    this.setState(
      {
        isLoggedIn: false,
        userData: null,
        authToken: null
      },
      callback
    );
  };

  error = errorObject => {
    if (this.state.hasError) return;
    if (errorObject) {
      logError(errorObject);
    }
    this.setState({
      hasError: true,
      errorObject
    });
  };

  setInputLogin = combo => this.setState({ inputLoginCombo: combo });

  render() {
    const {
      isReady,
      authToken,
      masterData,
      storeProperties,
      isLoadingStoreProperties,
      serviceCopies,
      hasError,
      isLoggedIn,
      errorObject,
      userData,
      aspenBenefits,
      allowPhoneLogin,
      inputLoginCombo,
      allowsExchange,
      canCreateRequest,
      allowsDiscountForwarding,
      isDemoApp
    } = this.state;

    if (!isReady || isLoadingStoreProperties) {
      return <Loader customClass="app-loader" />;
    }

    return (
      <AuthContext.Provider
        value={{
          hasError,
          isDemoApp,
          authToken,
          isLoggedIn,
          masterData,
          errorObject,
          aspenBenefits,
          serviceCopies,
          allowsExchange,
          storeProperties,
          allowPhoneLogin,
          inputLoginCombo,
          canCreateRequest,
          allowsDiscountForwarding,
          appType: this.appType,
          userData: isLoggedIn ? userData : null,
          setInputLogin: this.setInputLogin,
          login: this.login,
          logout: this.logout,
          appError: this.error,
          getLoginOrderID: this.getLoginOrderID,
          getStoreDetails: () => this.storeDetails
        }}
      >
        {this.props.children}
      </AuthContext.Provider>
    );
  }
}

const AuthConsumer = AuthContext.Consumer;

const ConnectedAuthProvider = withRouter(AuthProvider);

export { ConnectedAuthProvider as AuthProvider, AuthConsumer, AuthContext };
